'use client'

import React, { useState } from 'react'
import { X, Printer, Eye } from 'lucide-react'
import { generateLabelPDF, printPDF } from '../../lib/printService'

interface PrintTemplate {
  id?: number
  name: string
  width: number
  height: number
  scenario: string | null
  zones: any[]
}

interface TestPrintDialogProps {
  isOpen: boolean
  onClose: () => void
  template: PrintTemplate
}

export default function TestPrintDialog({ isOpen, onClose, template }: TestPrintDialogProps) {
  const [quantity, setQuantity] = useState(1)
  const [isPrinting, setIsPrinting] = useState(false)
  const [testData, setTestData] = useState<{ [key: string]: string }>({})

  // Extract all field mappings from zones
  const fieldMappings = React.useMemo(() => {
    const mappings = new Set<string>()
    template.zones.forEach(zone => {
      if (zone.field_mapping) {
        mappings.add(zone.field_mapping)
      }
    })
    return Array.from(mappings).sort()
  }, [template.zones])

  // Initialize test data with sample values based on field names
  React.useEffect(() => {
    const initialData: { [key: string]: string } = {}
    fieldMappings.forEach(field => {
      const lower = field.toLowerCase()
      if (lower.includes('barcode') || lower === 'ic' || lower.includes('_ic')) {
        initialData[field] = '12345'
      } else if (lower.includes('code')) {
        initialData[field] = '12345'
      } else if (lower.includes('name') || lower.includes('title')) {
        initialData[field] = 'Sample Item Name'
      } else if (lower.includes('price')) {
        initialData[field] = '$99.99'
      } else if (lower.includes('quantity') || lower.includes('qty')) {
        initialData[field] = '5'
      } else if (lower.includes('location')) {
        initialData[field] = 'Warehouse A'
      } else if (lower.includes('category')) {
        initialData[field] = 'Electronics'
      } else if (lower.includes('brand')) {
        initialData[field] = 'Brand Name'
      } else if (lower.includes('size')) {
        initialData[field] = 'Medium'
      } else if (lower.includes('color')) {
        initialData[field] = 'Blue'
      } else if (lower.includes('condition')) {
        initialData[field] = 'New'
      } else if (lower.includes('date')) {
        initialData[field] = new Date().toLocaleDateString()
      } else {
        initialData[field] = 'Sample Text'
      }
    })
    setTestData(initialData)
  }, [fieldMappings])

  const handlePreview = async () => {
    setIsPrinting(true)
    try {
      const pdf = await generateLabelPDF(template, testData, 1)
      window.open(pdf.output('bloburl'), '_blank')
    } catch (error) {
      console.error('Error generating preview:', error)
      alert('Failed to generate preview')
    } finally {
      setIsPrinting(false)
    }
  }

  const handlePrint = async () => {
    setIsPrinting(true)
    try {
      const pdf = await generateLabelPDF(template, testData, quantity)
      printPDF(pdf)
      onClose()
    } catch (error) {
      console.error('Error printing:', error)
      alert('Failed to print label')
    } finally {
      setIsPrinting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        backdropFilter: 'blur(4px)'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'linear-gradient(to bottom, #0a0e1a, #1a1f2e)',
          border: '2px solid rgba(255, 217, 61, 0.5)',
          borderRadius: '16px',
          width: '90%',
          maxWidth: '700px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 0 40px rgba(255, 217, 61, 0.3)',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '24px',
            borderBottom: '1px solid rgba(255, 217, 61, 0.2)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div>
            <h2
              style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '1.75rem',
                fontWeight: 700,
                color: '#FFD93D',
                margin: 0,
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}
            >
              🧪 Test Print
            </h2>
            <p
              style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '1rem',
                color: '#8A95AA',
                margin: '8px 0 0 0'
              }}
            >
              {template.name} ({template.width}" × {template.height}")
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: '2px solid rgba(255, 217, 61, 0.3)',
              borderRadius: '8px',
              color: '#FFD93D',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255, 217, 61, 0.6)'
              e.currentTarget.style.background = 'rgba(255, 217, 61, 0.1)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255, 217, 61, 0.3)'
              e.currentTarget.style.background = 'transparent'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {/* Instructions */}
          <div
            style={{
              background: 'rgba(255, 217, 61, 0.05)',
              border: '1px solid rgba(255, 217, 61, 0.2)',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}
          >
            <p
              style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '0.875rem',
                color: '#E0E6F0',
                margin: 0
              }}
            >
              💡 Enter sample data for each field in your template. The values will be used to generate a test label.
            </p>
          </div>

          {/* Test Data Fields */}
          {fieldMappings.length > 0 ? (
            <div style={{ marginBottom: '24px' }}>
              <label
                style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#FFD93D',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  display: 'block',
                  marginBottom: '12px'
                }}
              >
                Sample Data
              </label>
              <div style={{ display: 'grid', gap: '12px' }}>
                {fieldMappings.map(field => (
                  <div key={field}>
                    <label
                      style={{
                        fontFamily: 'Rajdhani, sans-serif',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#8A95AA',
                        textTransform: 'uppercase',
                        display: 'block',
                        marginBottom: '4px'
                      }}
                    >
                      {field}
                    </label>
                    <input
                      type="text"
                      value={testData[field] || ''}
                      onChange={(e) => setTestData({ ...testData, [field]: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: '#242938',
                        border: '2px solid rgba(255, 217, 61, 0.3)',
                        borderRadius: '6px',
                        color: '#E0E6F0',
                        fontFamily: 'Rajdhani, sans-serif',
                        fontSize: '0.875rem',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 217, 61, 0.6)'
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 217, 61, 0.3)'
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div
              style={{
                background: 'rgba(148, 163, 184, 0.1)',
                border: '1px solid rgba(148, 163, 184, 0.2)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px',
                textAlign: 'center'
              }}
            >
              <p
                style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '0.875rem',
                  color: '#8A95AA',
                  margin: 0
                }}
              >
                No field mappings found in this template. Add zones with field mappings to test with dynamic data.
              </p>
            </div>
          )}

          {/* Quantity */}
          <div style={{ marginBottom: '24px' }}>
            <label
              style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#FFD93D',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                display: 'block',
                marginBottom: '8px'
              }}
            >
              Quantity
            </label>
            <input
              type="number"
              min="1"
              max="100"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, Math.min(100, Number(e.target.value))))}
              style={{
                width: '100%',
                padding: '12px',
                background: '#242938',
                border: '2px solid rgba(255, 217, 61, 0.3)',
                borderRadius: '8px',
                color: '#E0E6F0',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '1rem',
                outline: 'none'
              }}
            />
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handlePreview}
              disabled={isPrinting}
              style={{
                flex: 1,
                padding: '12px 24px',
                background: '#242938',
                border: '2px solid rgba(255, 217, 61, 0.5)',
                borderRadius: '8px',
                color: '#FFD93D',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '1rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                cursor: isPrinting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                opacity: isPrinting ? 0.5 : 1,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!isPrinting) {
                  e.currentTarget.style.background = 'rgba(255, 217, 61, 0.1)'
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#242938'
              }}
            >
              <Eye size={18} />
              Preview
            </button>
            <button
              onClick={handlePrint}
              disabled={isPrinting}
              style={{
                flex: 1,
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #FFD93D, #FFA500)',
                border: '2px solid rgba(255, 217, 61, 0.6)',
                borderRadius: '8px',
                color: '#0a0e1a',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '1rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                cursor: isPrinting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                opacity: isPrinting ? 0.5 : 1,
                boxShadow: '0 0 20px rgba(255, 217, 61, 0.4)',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!isPrinting) {
                  e.currentTarget.style.boxShadow = '0 0 30px rgba(255, 217, 61, 0.6)'
                  e.currentTarget.style.transform = 'translateY(-2px)'
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 217, 61, 0.4)'
                e.currentTarget.style.transform = 'translateY(0)'
              }}
            >
              <Printer size={18} />
              {isPrinting ? 'Generating...' : `Print ${quantity} Label${quantity > 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
